<template>
  <div
    class="bg-white dark:bg-gray-800 sm:rounded-lg sm:shadow dark:sm:shadow-gray-900/50 relative"
  >
    <!-- Legend -->
    <div
      class="flex flex-wrap items-center gap-x-4 gap-y-1 px-3 sm:px-4 py-2 text-[11px] text-gray-600 dark:text-gray-300 border-b border-gray-200 dark:border-gray-700"
    >
      <span
        v-for="cat in visibleCategories"
        :key="cat.value"
        class="inline-flex items-center gap-1.5"
      >
        <span class="h-2.5 w-2.5 rounded-full" :class="cat.dot" />
        {{ cat.label }}
      </span>
      <span class="inline-flex items-center gap-1.5 text-gray-400 dark:text-gray-500">
        <span class="h-2.5 w-4 rounded-sm" :style="{ backgroundColor: colorForValue(4) }" />
        Beschikbaar (dagen/week)
      </span>
    </div>

    <div
      ref="scrollRef"
      class="timeline-scroll overflow-y-auto overflow-x-hidden"
      :style="{ maxHeight }"
    >
      <!-- Week header -->
      <div
        class="flex sticky top-0 z-20 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
      >
        <div
          class="flex-shrink-0 w-32 sm:w-44 md:w-60 lg:w-72 px-2 sm:px-4 py-2 text-left text-[10px] sm:text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
        >
          Collega
        </div>
        <div class="flex-1 grid" :style="weekColumnsStyle">
          <div
            v-for="(w, idx) in displayedWeeks"
            :key="idx"
            class="text-center text-[10px] font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider py-2 cursor-help"
            :title="`Week ${w.iso_label}: ${formatDate(w.week_start)} t/m ${formatDate(w.week_end)}`"
          >
            {{ w.iso_label }}
          </div>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="px-4 py-12 text-center">
        <div class="flex flex-col items-center justify-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <p class="mt-2 text-gray-600 dark:text-gray-400">Activiteiten laden…</p>
        </div>
      </div>

      <!-- Employee groups -->
      <template v-else>
        <div
          v-for="row in employeeRows"
          :key="row.emp.simplicate_id"
          class="border-t-2 border-gray-200 dark:border-gray-700"
        >
          <!-- Employee header: name + availability (free) strip -->
          <div class="flex items-stretch">
            <div
              class="flex-shrink-0 w-32 sm:w-44 md:w-60 lg:w-72 px-2 sm:px-4 py-1.5 sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
            >
              <div class="text-xs sm:text-sm font-semibold text-gray-900 dark:text-white truncate">
                {{ row.emp.name }}
              </div>
              <div class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 truncate">
                {{ row.emp.function || '—' }}
                <span v-if="row.teamNames" class="text-gray-400 dark:text-gray-500"
                  >· {{ row.teamNames }}</span
                >
              </div>
            </div>
            <div class="flex-1 grid py-1.5" :style="weekColumnsStyle">
              <div
                v-for="(f, idx) in row.free"
                :key="idx"
                class="mx-px h-[22px] flex items-center justify-center text-[10px] font-semibold rounded-sm"
                :style="row.weekUnavailable[idx] ? UNAVAILABLE_STYLE : freeCellStyle(f)"
                :title="
                  row.weekUnavailable[idx]
                    ? `Week ${displayedWeeks[idx]?.iso_label}: niet in dienst`
                    : `Week ${displayedWeeks[idx]?.iso_label}: ${fmtDpw(f)} dag(en) beschikbaar`
                "
              >
                {{ row.weekUnavailable[idx] ? '' : fmtDpw(f) }}
              </div>
            </div>
          </div>

          <!-- Item rows: one per project / pre-sourcing / voorstel -->
          <div v-for="item in row.items" :key="item.key" class="flex items-stretch">
            <!-- Label: name + organisation -->
            <div
              class="flex-shrink-0 w-32 sm:w-44 md:w-60 lg:w-72 px-2 sm:px-4 py-1 sticky left-0 z-10 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700"
              :title="`${item.name}${item.org ? ' · ' + item.org : ''}${item.role ? ' · ' + item.role : ''}`"
            >
              <div class="flex items-center gap-1.5 min-w-0 pl-2">
                <span class="h-2 w-2 rounded-full flex-shrink-0" :class="item.dot" />
                <span class="text-[11px] text-gray-800 dark:text-gray-200 truncate">{{
                  item.name
                }}</span>
              </div>
              <div
                v-if="item.org"
                class="text-[10px] text-gray-400 dark:text-gray-500 truncate pl-[14px]"
              >
                {{ item.org }}
              </div>
            </div>

            <!-- Timeline cells -->
            <div class="flex-1 relative py-1" :style="trackBackground">
              <!-- Projects: discrete per-week blocks (only weeks with hours) -->
              <div v-if="item.kind === 'blocks'" class="grid" :style="weekColumnsStyle">
                <div v-for="(d, idx) in item.blockDays" :key="idx" class="px-px h-[20px]">
                  <div
                    v-if="d > 0.05"
                    class="w-full h-full rounded-sm flex items-center justify-center text-[9px] font-semibold tabular-nums leading-none"
                    :class="item.barClass"
                    :title="`Week ${displayedWeeks[idx]?.iso_label}: ${fmtDpw(d)} dag(en)`"
                  >
                    {{ fmtDpw(d) }}
                  </div>
                </div>
              </div>

              <!-- Pre-sourcing / voorstel: continuous bar -->
              <div v-else class="relative h-[20px]">
                <div
                  class="absolute flex items-center px-1.5 h-full text-[10px] font-medium truncate cursor-default shadow-sm"
                  :class="[item.barClass, barRadius(item.bar)]"
                  :style="{ left: `${item.bar.leftPct}%`, width: `${item.bar.widthPct}%` }"
                  @mouseenter="showTooltip($event, item)"
                  @mousemove="moveTooltip($event)"
                  @mouseleave="hideTooltip"
                >
                  <span v-if="item.bar.continuesBefore" class="mr-0.5 opacity-80 flex-shrink-0"
                    >‹</span
                  >
                  <span v-if="item.bar.dpwText" class="font-semibold tabular-nums">{{
                    item.bar.dpwText
                  }}</span>
                  <span
                    v-if="item.bar.continuesAfter"
                    class="ml-auto pl-0.5 opacity-80 flex-shrink-0"
                    >›</span
                  >
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Hover tooltip (pre-sourcing / voorstel) -->
    <Teleport to="body">
      <div
        v-if="tooltip.visible"
        class="fixed z-[100] pointer-events-none max-w-xs rounded-lg bg-gray-900 dark:bg-gray-700 text-white text-xs shadow-xl px-3 py-2"
        :style="{ left: `${tooltip.x}px`, top: `${tooltip.y}px` }"
      >
        <div class="flex items-center gap-1.5 font-semibold mb-1">
          <span class="h-2 w-2 rounded-full flex-shrink-0" :class="tooltip.dot" />
          <span>{{ tooltip.title }}</span>
        </div>
        <div
          v-for="(line, i) in tooltip.lines"
          :key="i"
          class="text-gray-300 dark:text-gray-200 leading-snug"
        >
          {{ line }}
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, reactive, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { ACTIVITY_CATEGORY_OPTIONS } from '@/constants/activityCategories.js'
import { allocationHoursForWeek } from '@/utils/proposalAvailability.js'

const props = defineProps({
  employees: { type: Array, required: true },
  // weeks: [{ week_start, week_end, iso_label }]
  weeks: { type: Array, required: true },
  loading: { type: Boolean, default: false },
  categories: { type: Array, default: () => ACTIVITY_CATEGORY_OPTIONS.map(o => o.value) }
})

// Visual metadata per category (colors mirror EmployeeProjectsSidebar.vue)
const CATEGORY_META = {
  ziekte: {
    label: 'Ziekte',
    dot: 'bg-rose-500',
    barClass: 'bg-rose-500/90 text-white dark:bg-rose-600/90'
  },
  verlof: {
    label: 'Verlof',
    dot: 'bg-sky-500',
    barClass: 'bg-sky-500/90 text-white dark:bg-sky-600/90'
  },
  performance_expertise: {
    label: 'Performance & expertise',
    dot: 'bg-teal-500',
    barClass: 'bg-teal-500/90 text-white dark:bg-teal-600/90'
  },
  projects: {
    label: 'Projecten',
    dot: 'bg-green-500',
    barClass: 'bg-green-500/90 text-white dark:bg-green-600/90'
  },
  presourcings: {
    label: 'Pre-sourcings',
    dot: 'bg-purple-500',
    barClass: 'bg-purple-500/90 text-white dark:bg-purple-600/90'
  },
  voorstellen: {
    label: 'Voorstellen',
    dot: 'bg-yellow-400',
    barClass: 'bg-yellow-400/90 text-yellow-950 dark:bg-yellow-500/90 dark:text-yellow-950'
  }
}
// Absence categories render as per-week blocks at the top, before project work.
const ABSENCE_ORDER = ['ziekte', 'verlof', 'performance_expertise']
const CATEGORY_ORDER = [...ABSENCE_ORDER, 'projects', 'presourcings', 'voorstellen']

const visibleCategories = computed(() => {
  const selected = props.categories?.length ? props.categories : CATEGORY_ORDER
  return CATEGORY_ORDER.filter(c => selected.includes(c)).map(value => ({
    value,
    label: CATEGORY_META[value].label,
    dot: CATEGORY_META[value].dot
  }))
})

// Limit weeks on mobile to keep labels readable (matches WeeklyAvailabilityGrid)
const isMobile = ref(false)
const checkMobile = () => {
  isMobile.value = window.innerWidth < 640
}
const displayedWeeks = computed(() => {
  if (isMobile.value && props.weeks.length > 10) return props.weeks.slice(0, 10)
  return props.weeks
})
const weekCount = computed(() => displayedWeeks.value.length || 1)
const weekColumnsStyle = computed(() => ({
  gridTemplateColumns: `repeat(${weekCount.value}, minmax(0, 1fr))`
}))

// Subtle vertical week gridlines behind the bars
const trackBackground = computed(() => {
  const w = 100 / weekCount.value
  return {
    backgroundImage:
      'linear-gradient(to right, rgba(148,163,184,0.18) 0, rgba(148,163,184,0.18) 1px, transparent 1px)',
    backgroundSize: `${w}% 100%`
  }
})

// Map a date string (YYYY-MM-DD) to a week index. Clamps before→0, after→last.
const weekIndexForDate = dateStr => {
  const weeks = displayedWeeks.value
  if (!dateStr || weeks.length === 0) return null
  for (let i = 0; i < weeks.length; i++) {
    if (dateStr <= weeks[i].week_end) return i
  }
  return weeks.length - 1
}

const formatDate = input => {
  if (!input) return ''
  const s = String(input).split('T')[0]
  const d = new Date(`${s}T00:00:00`)
  if (isNaN(d)) return s
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`
}

const fmtDpw = n => {
  const r = Math.round(n * 10) / 10
  return r % 1 === 0 ? String(r) : r.toFixed(1)
}

// --- Availability colour scale (mirrors WeeklyAvailabilityGrid) ---
// More free days = redder (needs sourcing); fully booked = green.
const hexToRgb = hex => {
  const n = parseInt(hex.replace('#', ''), 16)
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 }
}
const rgbToHex = ({ r, g, b }) => {
  const h = x => x.toString(16).padStart(2, '0')
  return `#${h(r)}${h(g)}${h(b)}`
}
const lerp = (a, b, t) => a + (b - a) * t
const lerpColor = (c1, c2, t) => {
  const a = hexToRgb(c1)
  const b = hexToRgb(c2)
  return rgbToHex({
    r: Math.round(lerp(a.r, b.r, t)),
    g: Math.round(lerp(a.g, b.g, t)),
    b: Math.round(lerp(a.b, b.b, t))
  })
}
const stops = [
  { v: 0, c: '#16a34a' },
  { v: 2, c: '#eab308' },
  { v: 4, c: '#b91c1c' }
]
const colorForValue = val => {
  if (val < 0) return '#3b82f6' // overbooked
  const v = Math.max(0, Math.min(4, val ?? 0))
  if (v <= stops[1].v) return lerpColor(stops[0].c, stops[1].c, (v - 0) / (stops[1].v || 1))
  return lerpColor(stops[1].c, stops[2].c, (v - stops[1].v) / (stops[2].v - stops[1].v || 1))
}
const freeCellStyle = val => {
  const base = colorForValue(val)
  const { r, g, b } = hexToRgb(base)
  const lighten = x => Math.min(255, Math.round(x + (255 - x) * 0.15))
  return {
    backgroundColor: rgbToHex({ r: lighten(r), g: lighten(g), b: lighten(b) }),
    color: '#1f2937'
  }
}

// Grey striped pattern for weeks outside the employment period (matches the grid)
const UNAVAILABLE_STYLE = {
  backgroundColor: '#e5e7eb',
  backgroundImage:
    'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(156, 163, 175, 0.3) 3px, rgba(156, 163, 175, 0.3) 6px)',
  opacity: 0.7
}

// Geometry for a continuous bar (pre-sourcing / voorstel)
const buildBarGeom = item => {
  const N = weekCount.value
  const weeks = displayedWeeks.value
  const startIdx = weekIndexForDate(item.start_date)
  let endIdx = weekIndexForDate(item.end_date)
  if (startIdx === null || endIdx === null) return null
  if (endIdx < startIdx) endIdx = startIdx
  const dpw = Number(item.dpw) || 0
  return {
    startIdx,
    endIdx,
    leftPct: (startIdx / N) * 100,
    widthPct: ((endIdx - startIdx + 1) / N) * 100,
    continuesBefore: !!item.start_date && item.start_date < weeks[0].week_start,
    continuesAfter: !!item.end_date && item.end_date > weeks[N - 1].week_end,
    dpw,
    dpwText: dpw > 0 ? `${fmtDpw(dpw)}d/w` : ''
  }
}

const buildTooltipLines = item => {
  const lines = []
  if (item.role) lines.push(`Rol: ${item.role}`)
  if (item.organization_name) lines.push(item.organization_name)
  const dpw = Number(item.dpw)
  const period = `${formatDate(item.start_date)} – ${formatDate(item.end_date)}`
  lines.push(dpw ? `${dpw}d/w • ${period}` : period)
  return lines
}

const employeeRows = computed(() =>
  props.employees.map(emp => {
    const N = weekCount.value
    const selected = new Set(visibleCategories.value.map(c => c.value))
    const items = []
    const planned = new Array(N).fill(0)
    const contractStart = emp.contract_start_date
      ? String(emp.contract_start_date).split('T')[0]
      : null
    const contractEnd = emp.contract_end_date ? String(emp.contract_end_date).split('T')[0] : null

    // Absence (ziekte / verlof / performance & expertise): one row each at the
    // top, discrete per-week blocks. Already part of booked hours, so they do
    // NOT add to `planned` (would double-count the availability reduction).
    for (const cat of ABSENCE_ORDER) {
      if (!selected.has(cat)) continue
      const blockDays = new Array(N).fill(0)
      for (const w of emp[cat] || []) {
        const idx = weekIndexForDate(w.week_start)
        if (idx != null) blockDays[idx] += Number(w.days) || 0
      }
      if (!blockDays.some(d => d > 0.05)) continue
      items.push({
        key: `abs-${cat}`,
        dot: CATEGORY_META[cat].dot,
        barClass: CATEGORY_META[cat].barClass,
        name: CATEGORY_META[cat].label,
        org: null,
        role: null,
        kind: 'blocks',
        blockDays
      })
    }

    // Projects: one row each, discrete per-week blocks (weeks with hours)
    if (selected.has('projects')) {
      for (const p of emp.projects || []) {
        const blockDays = new Array(N).fill(0)
        for (const w of p.weeks || []) {
          const idx = weekIndexForDate(w.week_start)
          if (idx != null) blockDays[idx] += Number(w.days) || 0
        }
        items.push({
          key: `proj-${p.name}`,
          dot: CATEGORY_META.projects.dot,
          barClass: CATEGORY_META.projects.barClass,
          name: p.name,
          org: p.organization_name,
          role: null,
          kind: 'blocks',
          blockDays
        })
      }
    }

    // Pre-sourcings / voorstellen: one row each, continuous bar
    for (const cat of ['presourcings', 'voorstellen']) {
      if (!selected.has(cat)) continue
      for (const it of emp[cat] || []) {
        const bar = buildBarGeom(it)
        if (!bar) continue
        for (let w = bar.startIdx; w <= bar.endIdx; w++) {
          const week = displayedWeeks.value[w]
          planned[w] +=
            allocationHoursForWeek(it, week.week_start, week.week_end, contractStart, contractEnd) /
            8
        }
        items.push({
          key: `${cat}-${it.preference_id ?? it.prospect_id}-${it.start_date}`,
          category: cat,
          dot: CATEGORY_META[cat].dot,
          barClass: CATEGORY_META[cat].barClass,
          name: it.prospect_subject,
          org: it.organization_name,
          role: it.role,
          kind: 'bar',
          bar,
          lines: buildTooltipLines(it)
        })
      }
    }

    // Availability (free days) per week = prorated capacity − booked − selected planned.
    // Booked already includes project (PM) hours, so projects don't double-count.
    const defaultCapacity = Number(emp.max_days_per_week) || 0
    const capacity = displayedWeeks.value.map(week => {
      const serverCapacity = (emp.weekly_capacity || []).find(
        entry => entry.week_start === week.week_start
      )
      if (serverCapacity) return Number(serverCapacity.days) || 0

      // Backward-compatible fallback for cached/older API responses.
      const capacityHours = allocationHoursForWeek(
        {
          start_date: contractStart || week.week_start,
          end_date: contractEnd || week.week_end,
          dpw: defaultCapacity
        },
        week.week_start,
        week.week_end
      )
      return capacityHours / 8
    })
    const booked = new Array(N).fill(0)
    for (const w of emp.weekly_booked || []) {
      const idx = weekIndexForDate(w.week_start)
      if (idx != null) booked[idx] += Number(w.days) || 0
    }
    const free = booked.map((b, i) => Math.round((capacity[i] - b - planned[i]) * 10) / 10)

    // Weeks outside the employment period → greyed out (not available), like the grid
    const weekUnavailable = displayedWeeks.value.map(w => {
      const pastEnd = contractEnd && w.week_start > contractEnd
      const beforeStart = contractStart && w.week_end < contractStart
      return !!(pastEnd || beforeStart)
    })

    return {
      emp,
      teamNames: (emp.teams || []).map(t => t.name).join(', '),
      items,
      free,
      weekUnavailable,
      isEmpty: items.length === 0
    }
  })
)

const barRadius = bar => {
  if (bar.continuesBefore && bar.continuesAfter) return 'rounded-none'
  if (bar.continuesBefore) return 'rounded-r'
  if (bar.continuesAfter) return 'rounded-l'
  return 'rounded'
}

// Tooltip following the cursor
const tooltip = reactive({ visible: false, x: 0, y: 0, title: '', lines: [], dot: '' })
const positionTooltip = e => {
  const offset = 14
  let x = e.clientX + offset
  let y = e.clientY + offset
  if (x > window.innerWidth - 280) x = e.clientX - 280
  if (y > window.innerHeight - 120) y = e.clientY - 120
  tooltip.x = Math.max(8, x)
  tooltip.y = Math.max(8, y)
}
const showTooltip = (e, item) => {
  tooltip.title = item.name
  tooltip.lines = item.lines || []
  tooltip.dot = item.dot
  tooltip.visible = true
  positionTooltip(e)
}
const moveTooltip = e => {
  if (tooltip.visible) positionTooltip(e)
}
const hideTooltip = () => {
  tooltip.visible = false
}

// Scroll height management (mirrors WeeklyAvailabilityGrid)
const scrollRef = ref(null)
const maxHeight = ref('none')
const calculateMaxHeight = () => {
  if (!scrollRef.value) return
  const rect = scrollRef.value.getBoundingClientRect()
  maxHeight.value = `${window.innerHeight - rect.top - 16}px`
}
onMounted(() => {
  checkMobile()
  nextTick(calculateMaxHeight)
  window.addEventListener('resize', calculateMaxHeight)
  window.addEventListener('resize', checkMobile)
})
onBeforeUnmount(() => {
  window.removeEventListener('resize', calculateMaxHeight)
  window.removeEventListener('resize', checkMobile)
})
watch(
  () => props.employees,
  () => nextTick(calculateMaxHeight)
)
</script>

<style scoped>
.timeline-scroll {
  overscroll-behavior: contain;
  scrollbar-width: auto;
  scrollbar-color: #94a3b8 #f1f5f9;
}
.timeline-scroll::-webkit-scrollbar {
  width: 14px;
}
.timeline-scroll::-webkit-scrollbar-thumb {
  background-color: #94a3b8;
  border-radius: 9999px;
  border: 3px solid #e2e8f0;
}
.timeline-scroll::-webkit-scrollbar-track {
  background: #f1f5f9;
}
:global(.dark) .timeline-scroll {
  scrollbar-color: #4b5563 #374151;
}
:global(.dark) .timeline-scroll::-webkit-scrollbar-thumb {
  background-color: #4b5563;
  border-color: #374151;
}
:global(.dark) .timeline-scroll::-webkit-scrollbar-track {
  background: #374151;
}
</style>
